import React, { useState, useEffect } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import ApiService from 'helpers/apiService';
import { hasAccess } from 'helpers/access';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from 'uuid';

// Размер кнопок-иконок в строке оператора (компактные действия).
const ICON_BTN_STYLE = { fontSize: '0.8rem', padding: '2px 7px', lineHeight: 1 };

// dedupById: фильтр массива по уникальным `id`. Защита от дублирующихся
// записей в API-ответе (backend deploy/seed разнобой) — иначе React
// падает на `Encountered two children with the same key`.
function dedupById<T extends { id?: string }>(arr: T[] | undefined): T[] {
  if (!Array.isArray(arr)) return [];
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of arr) {
    const id = item?.id;
    if (typeof id !== 'string' || seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}

const ProfilesList: React.FC = () => {
  const [operators, setOperators] = useState<any[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  // enabled хранится отдельно от selectedOperator — toggle "Активен" не должен
  // пересоздавать оператора и сбрасывать выбранную роль через useEffect ниже.
  const [enabled, setEnabled] = useState(true);

  // Пароли операторов: сброс (генерит+email) и временный пароль (must_change).
  const [pwBusyId, setPwBusyId] = useState<string | null>(null);
  const [showTempModal, setShowTempModal] = useState(false);
  const [tempOperator, setTempOperator] = useState<any | null>(null);
  const [tempPassword, setTempPassword] = useState('');
  const [tempBusy, setTempBusy] = useState(false);
  const [tempDone, setTempDone] = useState(false);

  // Отдельное право на сброс/выдачу временного пароля (отдельно от profileEdit).
  const canResetPassword = hasAccess('profileResetPassword');

  // resolveRoleId — устойчиво восстанавливает id роли оператора из любого
  // формата ответа/локального состояния. Кандидаты (по приоритету): group,
  // roleId, data[0] (в т.ч. двойно-обёрнутый '["1"]'), затем элементы roles.
  // Берём ПЕРВЫЙ, который реально есть в списке ролей (rolesList). Это
  // отсекает мусор: у legacy-операторов в profile_role лежат КОДЫ ДОСТУПА
  // (accountsGet и т.п.), а не id роли — без валидации они попадали в
  // dropdown и роль «сбрасывалась» в пусто.
  const resolveRoleId = (operator: any): string | null => {
    if (!operator) return null;
    const known = new Set(rolesList.map((r: any) => r.id));
    const cands: string[] = [];
    const push = (v: any) => {
      if (typeof v === 'string' && v.trim()) cands.push(v.trim());
    };
    push(operator.group);
    push(operator.roleId);
    const d = operator.data;
    if (Array.isArray(d) && d.length > 0) {
      let first = d[0];
      if (typeof first === 'string' && first.trim().startsWith('[')) {
        try {
          const inner = JSON.parse(first);
          if (Array.isArray(inner) && inner.length > 0) first = inner[0];
        } catch {
          /* оставляем как есть */
        }
      }
      push(first);
    }
    if (Array.isArray(operator.roles)) operator.roles.forEach(push);
    for (const c of cands) if (known.has(c)) return c;
    return null;
  };

  // Имя роли оператора для таблицы (показываем роль, а не «сырой» список прав).
  const roleNameOf = (operator: any): string => {
    const roleId = resolveRoleId(operator);
    if (!roleId) return '—';
    const role = rolesList.find((r: any) => r.id === roleId);
    return role?.description || String(roleId);
  };

  const requestId = uuidv4();

  const fetchOperators = async () => {
    try {
      const response = await ApiService.getOperators<any>(requestId);
      setOperators(response.data);
    } catch (error) {
      console.error('Failed to fetch operators:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesResponse = await ApiService.getListRoles(requestId);
      setRolesList(dedupById(rolesResponse.data));
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  useEffect(() => {
    fetchOperators();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedOperator) {
      // Роль восстанавливаем устойчиво (resolveRoleId): group → roleId →
      // data[0] → валидный элемент roles, сверяя с rolesList. Зависимость от
      // rolesList — чтобы пересчитать, если роли догрузились после выбора.
      setSelectedRoleId(resolveRoleId(selectedOperator));
      setEnabled(selectedOperator.status === 'active');
    } else {
      setSelectedRoleId(null);
      setEnabled(true); // дефолт для нового оператора — активен
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperator, rolesList]);

  const handleRoleChange = (roleId: string) => {
    setSelectedRoleId(roleId);
  };

  const openAdd = () => {
    setSelectedOperator(null);
    setShowModal(true);
  };

  const openEdit = (operator: any) => {
    setSelectedOperator(operator);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedOperator(null);
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;

    const nameInput = form.elements.namedItem('name') as HTMLInputElement;
    const phoneInput = form.elements.namedItem('phone') as HTMLInputElement;
    const emailInput = form.elements.namedItem('email') as HTMLInputElement;
    const profileTypeEl = form.elements.namedItem('profileType') as HTMLSelectElement;
    const groupEl = form.elements.namedItem('group') as HTMLSelectElement;

    const name = nameInput.value.trim();
    const phone = phoneInput.value.trim();
    const email = emailInput.value.trim();

    if (!name) {
      alert('Укажите ФИО оператора');
      return;
    }
    // Телефон уникален в БД (profiles_phone_uindex). Раньше пустой телефон
    // дефолтился на '+71234567890' для всех — второй такой оператор падал на
    // unique constraint (500). Теперь требуем явный телефон.
    if (!phone) {
      alert('Укажите телефон оператора — он используется как уникальный логин');
      return;
    }
    // Оператору назначается РОЛЬ (не список отдельных прав) — права берутся из
    // роли. Без роли у оператора не будет доступов, поэтому требуем выбор.
    const roleId = groupEl.value || selectedRoleId || '';
    if (!roleId) {
      alert('Выберите роль оператора');
      return;
    }

    const newOperator = {
      id: selectedOperator ? selectedOperator.id : uuidv4(),
      name,
      phone,
      email: email || '',
      profileType: profileTypeEl.value,
      group: roleId,
      enabled,
      // roles = [roleId]: бэкенд кладёт это в profile_role → JWT claim `r`/`acc`
      // резолвятся через role_access. Так оператор получает права от роли.
      roles: [roleId],
      roleId,
      status: enabled ? 'active' : 'blocked'
    };

    try {
      if (selectedOperator) {
        await ApiService.editOperator(newOperator, requestId);
        setOperators(operators.map(op => op.id === selectedOperator.id ? newOperator : op));
      } else {
        await ApiService.addOperator(newOperator, requestId);
        setOperators([...operators, newOperator]);
      }
      handleClose();
    } catch (error) {
      console.error('Failed to save operator:', error);
    }
  };

  const handleDelete = async (operator: any) => {
    if (!window.confirm('Удалить оператора «' + operator.name + '»?')) return;
    try {
      await ApiService.deleteOperator(operator.id, requestId);
      setOperators(operators.filter(op => op.id !== operator.id));
    } catch (error) {
      console.error('Failed to delete operator:', error);
    }
  };

  // Админ-сброс пароля оператору: генерится временный пароль, отправляется ему
  // на email, выставляется флаг обязательной смены при первом входе.
  const handleResetPassword = async (operator: any) => {
    if (!window.confirm(
      'Сбросить пароль оператору «' + operator.name + '»?\n' +
      'Новый временный пароль будет отправлен на email; оператор обязан сменить его при первом входе.'
    )) return;
    setPwBusyId(operator.id);
    try {
      await ApiService.profileResetPasswordAdmin(operator.id);
      alert('Пароль сброшен. Временный пароль отправлен оператору на email.');
    } catch (error: any) {
      console.error('Failed to reset operator password:', error);
      alert('Не удалось сбросить пароль: ' + (error?.response?.data?.message || error?.message || 'ошибка'));
    } finally {
      setPwBusyId(null);
    }
  };

  // Генератор временного пароля (12 символов: буквы/цифры/спецсимволы).
  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    const arr = new Uint32Array(14);
    (window.crypto || (window as any).msCrypto).getRandomValues(arr);
    let out = '';
    for (let i = 0; i < arr.length; i++) out += chars[arr[i] % chars.length];
    setTempPassword(out);
  };

  const openTempModal = (operator: any) => {
    setTempOperator(operator);
    setTempPassword('');
    setTempDone(false);
    setShowTempModal(true);
  };

  const handleSaveTempPassword = async () => {
    if (!tempOperator || !tempPassword) return;
    setTempBusy(true);
    try {
      await ApiService.profileSetTempPassword(tempOperator.id, tempPassword);
      setTempDone(true);
    } catch (error: any) {
      console.error('Failed to set temp password:', error);
      alert('Не удалось задать временный пароль: ' + (error?.response?.data?.message || error?.message || 'ошибка'));
    } finally {
      setTempBusy(false);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h3 className="mb-0">Операторы</h3>
          <p className="mt-1 mb-0 text-muted small">Управление операторами системы</p>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">Список операторов</Card.Title>
          <Button variant="primary" size="sm" onClick={openAdd}>
            + Добавить оператора
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
            <thead className="table-light">
              <tr>
                <th>ФИО</th>
                <th>Тип профиля</th>
                <th>Статус</th>
                <th>Роль</th>
                <th style={{ width: 240 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {operators.map((operator, index) => (
                <tr key={index}>
                  <td>{operator.name}</td>
                  <td>{operator.type === '1' ? 'Оператор' : 'Фин. контролер'}</td>
                  <td>
                    <Badge bg={operator.status === 'active' ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                      {operator.status === 'active' ? 'Активен' : 'Заблокирован'}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg="info" text="dark" style={{ fontSize: '0.7rem' }}>
                      {roleNameOf(operator)}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={ICON_BTN_STYLE}
                        onClick={() => openEdit(operator)}
                        title="Редактировать"
                        aria-label="Редактировать оператора"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />
                      </Button>
                      {canResetPassword && (
                        <>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            style={ICON_BTN_STYLE}
                            disabled={pwBusyId === operator.id}
                            onClick={() => handleResetPassword(operator)}
                            title="Сбросить пароль (временный отправится на email)"
                            aria-label="Сбросить пароль"
                          >
                            <FontAwesomeIcon icon={pwBusyId === operator.id ? 'spinner' : 'user-shield'} spin={pwBusyId === operator.id} />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={ICON_BTN_STYLE}
                            onClick={() => openTempModal(operator)}
                            title="Временный пароль (смена при первом входе)"
                            aria-label="Временный пароль"
                          >
                            <FontAwesomeIcon icon="key" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        style={ICON_BTN_STYLE}
                        onClick={() => handleDelete(operator)}
                        title="Удалить"
                        aria-label="Удалить оператора"
                      >
                        <FontAwesomeIcon icon="trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {operators.length === 0 && (
                <tr><td colSpan={5} className="text-muted text-center py-3">Нет доступных операторов</td></tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedOperator ? 'Редактировать оператора' : 'Добавить оператора'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleFormSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small">ФИО <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    name="name"
                    defaultValue={selectedOperator ? selectedOperator.name : ''}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Телефон <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    size="sm"
                    type="tel"
                    name="phone"
                    defaultValue={selectedOperator ? selectedOperator.phone : ''}
                    placeholder="+71234567890"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Email</Form.Label>
                  <Form.Control
                    size="sm"
                    type="email"
                    name="email"
                    defaultValue={selectedOperator ? selectedOperator.email : ''}
                    placeholder="example@domain.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Тип профиля</Form.Label>
                  <Form.Select
                    size="sm"
                    name="profileType"
                    defaultValue={selectedOperator ? selectedOperator.profileType : ''}
                  >
                    <option value="1">Оператор</option>
                    <option value="0">Фин. контролёр</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Роль <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    size="sm"
                    name="group"
                    value={selectedRoleId || ''}
                    onChange={e => handleRoleChange(e.target.value)}
                  >
                    <option value="">Выбрать</option>
                    {rolesList.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.description}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                    Права оператора определяются ролью. Управление правами роли — в разделе «Справочники → Роли».
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="enabled"
                  name="enabled"
                  label="Активен"
                  checked={enabled}
                  onChange={() => setEnabled((prev) => !prev)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={handleClose}>
              Отмена
            </Button>
            <Button variant="primary" size="sm" type="submit">
              Сохранить
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showTempModal} onHide={() => setShowTempModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Временный пароль оператора</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            Оператор: <strong>{tempOperator?.name}</strong>
            {tempOperator?.email ? ` (${tempOperator.email})` : ''}
          </p>
          {!tempDone ? (
            <>
              <Form.Group className="mb-2">
                <Form.Label className="small">Временный пароль</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    size="sm"
                    type="text"
                    value={tempPassword}
                    onChange={e => setTempPassword(e.target.value)}
                    placeholder="Введите или сгенерируйте"
                  />
                  <Button variant="outline-secondary" size="sm" onClick={generateTempPassword} type="button">
                    Сгенерировать
                  </Button>
                </div>
              </Form.Group>
              <p className="small text-muted mb-0">
                Оператор обязан будет сменить этот пароль при первом входе в админ-панель.
                Передайте его оператору безопасным способом — в ответе он показывается один раз.
              </p>
            </>
          ) : (
            <div className="alert alert-success mb-0">
              <p className="mb-1"><strong>Временный пароль установлен.</strong></p>
              <p className="small mb-1">Передайте оператору:</p>
              <code style={{ fontSize: '0.95rem' }}>{tempPassword}</code>
              <p className="small text-muted mt-2 mb-0">При первом входе оператор будет обязан сменить пароль.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowTempModal(false)}>
            {tempDone ? 'Закрыть' : 'Отмена'}
          </Button>
          {!tempDone && (
            <Button variant="primary" size="sm" disabled={tempBusy || !tempPassword} onClick={handleSaveTempPassword}>
              {tempBusy ? 'Сохранение...' : 'Установить'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfilesList;
